import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { RowSelectionState } from '@tanstack/react-table';
import {
  ChevronDown,
  FileBarChart2,
  FolderPlus,
  LayoutDashboard,
  Plus,
  Search,
} from 'lucide-react';
import { overlay } from 'overlay-kit';
import { startTransition, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useDebounce } from 'use-debounce';

import Modal from '@/components/Modal/Modal';
import { Input } from '@/components/shadcn/ui/input';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/shadcn/ui/pagination';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/shadcn/ui/popover';
import {
  aggregatedReportFolderListQueryOptions,
  aggregatedReportListQueryOptions,
  createAggregatedReportFolderMutationOptions,
} from '@/entities/report';
import { errorHandler } from '@/error-handler';
import CreateReportModal from '@/features/Report/components/CreateReportModal/CreateReportModal';
import SelectDashboardModal from '@/features/Report/components/SelectDashboardModal/SelectDashboardModal';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import { cn } from '@/lib/cn';
import { Button, PageHeader } from '@/shared/ui';
import { DataTable, DataTableEmpty } from '@/shared/ui/data-table';
import { toast } from '@/stores/pure/ToastStore';

import { ReportFolderFormDialog } from './report-folder-form-dialog';
import {
  calculateCombinedListPagination,
  createReportListRows,
  filterReportFolders,
  type ReportListRow,
} from './report-list-row';
import { ReportMoveActionBar } from './report-move-action-bar';
import { useReportTable } from './use-report-table';

const PAGE_SIZE = 10;
const MODAL_EXIT_DURATION_MS = 200;

const getVisiblePages = (currentPage: number, totalPages: number) => {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 3) {
    return [1, 2, 3, 4, 'ellipsis', totalPages] as const;
  }

  if (currentPage >= totalPages - 2) {
    return [
      1,
      'ellipsis',
      totalPages - 3,
      totalPages - 2,
      totalPages - 1,
      totalPages,
    ] as const;
  }

  return [
    1,
    'ellipsis-left',
    currentPage - 1,
    currentPage,
    currentPage + 1,
    'ellipsis-right',
    totalPages,
  ] as const;
};

function OverlayModalBridge({
  isOpen,
  close,
  unmount,
  children,
}: {
  isOpen: boolean;
  close: () => void;
  unmount: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    if (isOpen) return;

    const timeoutId = window.setTimeout(unmount, MODAL_EXIT_DURATION_MS);
    return () => window.clearTimeout(timeoutId);
  }, [isOpen, unmount]);

  return (
    <Modal status={isOpen} closeModal={close}>
      {children}
    </Modal>
  );
}

const ReportListPage = () => {
  const { workspace_id: workspaceId = '' } = useParams();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentFolderId = searchParams.get('folder_id') ?? undefined;
  const wr = useWorkspaceRouter();
  const queryClient = useQueryClient();
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchInputValue] = useDebounce(searchInputValue, 500);
  const [currentPage, setCurrentPage] = useState(1);
  const [createPopoverOpen, setCreatePopoverOpen] = useState(false);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  useEffect(() => {
    if (debouncedSearchInputValue === searchText) return;

    startTransition(() => setSearchText(debouncedSearchInputValue));
  }, [debouncedSearchInputValue, searchText]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText]);

  useEffect(() => {
    setSearchInputValue('');
    setSearchText('');
    setCurrentPage(1);
  }, [currentFolderId]);

  useEffect(() => {
    setRowSelection({});
  }, [currentFolderId, currentPage, searchText]);

  useEffect(() => {
    return () => overlay.closeAll();
  }, [pathname]);

  const folderListQuery = useQuery(
    aggregatedReportFolderListQueryOptions({ workspace_id: workspaceId }),
  );
  const folders = useMemo(
    () => folderListQuery.data?.items ?? [],
    [folderListQuery.data?.items],
  );
  const filteredFolders = useMemo(
    () =>
      currentFolderId ? [] : filterReportFolders(folders, searchText),
    [currentFolderId, folders, searchText],
  );
  const leadingItemCount = filteredFolders.length;
  const {
    leadingStart,
    leadingEnd,
    reportOffset,
    reportLimit,
  } = calculateCombinedListPagination({
    pageIndex: currentPage - 1,
    pageSize: PAGE_SIZE,
    leadingItemCount,
  });
  const reportListQuery = useQuery({
    ...aggregatedReportListQueryOptions({
      workspace_id: workspaceId,
      limit: Math.max(1, reportLimit),
      offset: reportOffset,
      order_by: 'desc',
      sort_by: 'updated_at',
      title: searchText.trim() || undefined,
      folder_id: currentFolderId,
    }),
    enabled:
      Boolean(workspaceId) &&
      (Boolean(currentFolderId) || folderListQuery.isSuccess),
    placeholderData: keepPreviousData,
  });
  const createFolderMutation = useMutation(
    createAggregatedReportFolderMutationOptions(queryClient),
  );
  const foldersOnPage = useMemo(
    () => filteredFolders.slice(leadingStart, leadingEnd),
    [filteredFolders, leadingEnd, leadingStart],
  );
  const reports = useMemo(
    () => (reportLimit > 0 ? (reportListQuery.data?.items ?? []) : []),
    [reportLimit, reportListQuery.data?.items],
  );
  const totalCount = reportListQuery.data?.total_count ?? 0;
  const combinedTotalCount = leadingItemCount + totalCount;
  const totalPages =
    combinedTotalCount > 0 ? Math.ceil(combinedTotalCount / PAGE_SIZE) : 0;
  const rows = useMemo<ReportListRow[]>(
    () =>
      createReportListRows({
        folders: foldersOnPage,
        reports,
        includeParentRow: Boolean(currentFolderId),
      }),
    [currentFolderId, foldersOnPage, reports],
  );
  const selectedReports = useMemo(
    () =>
      rows.flatMap((row) =>
        row.kind === 'report' && rowSelection[row.id] ? [row.report] : [],
      ),
    [rowSelection, rows],
  );
  const { columns } = useReportTable({ workspaceId });

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const navigateToFolder = (folderId?: string) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    if (folderId) nextSearchParams.set('folder_id', folderId);
    else nextSearchParams.delete('folder_id');
    setSearchParams(nextSearchParams);
  };

  const openCreateReportOverlay = (dashboardAlias: string) => {
    overlay.open(({ isOpen, close, unmount }) => (
      <OverlayModalBridge isOpen={isOpen} close={close} unmount={unmount}>
        <CreateReportModal dashboard_alias={dashboardAlias} />
      </OverlayModalBridge>
    ));
  };

  const openSelectDashboardOverlay = () => {
    overlay.open(({ isOpen, close, unmount }) => (
      <OverlayModalBridge isOpen={isOpen} close={close} unmount={unmount}>
        <SelectDashboardModal
          onConfirm={(dashboard) => {
            close();
            openCreateReportOverlay(dashboard.alias);
          }}
        />
      </OverlayModalBridge>
    ));
  };

  const openCreateFolderOverlay = () => {
    overlay.open(({ isOpen, close, unmount }) => (
      <ReportFolderFormDialog
        mode="create"
        isOpen={isOpen}
        close={close}
        unmount={unmount}
        onSubmit={async (name) => {
          try {
            await createFolderMutation.mutateAsync({
              workspace_id: workspaceId,
              name,
            });
            toast('Folder created', 'success');
          } catch (error) {
            toast(errorHandler(error, 'Failed to create folder'), 'fail');
            throw error;
          }
        }}
      />
    ));
  };

  const isEmptyFolder = Boolean(currentFolderId) && totalCount === 0;

  return (
    <div className="relative flex h-full min-h-0 flex-col overflow-hidden">
      <PageHeader
        title="Reports"
        desc="Share insights across your teams with reports."
        topRightNode={
          <Popover open={createPopoverOpen} onOpenChange={setCreatePopoverOpen}>
            <PopoverTrigger asChild>
              <span>
                <Button className="flex items-center" size="lg">
                  <Plus className="size-4" />
                  New Report
                  <ChevronDown className="size-4 opacity-70" />
                </Button>
              </span>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              className="flex w-[280px] flex-col gap-1 p-1.5"
            >
              <button
                className="flex cursor-pointer items-start gap-2.5 rounded-md px-3 py-2.5 text-left transition-colors hover:bg-black/5"
                onClick={() => {
                  setCreatePopoverOpen(false);
                  openSelectDashboardOverlay();
                }}
                type="button"
              >
                <LayoutDashboard className="mt-0.5 size-4 shrink-0 text-card-foreground" />
                <div className="flex flex-col gap-0.5">
                  <span className="text-sm font-medium text-foreground">
                    Create with Dashboard
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Based on an existing dashboard
                  </span>
                </div>
              </button>
              <button
                className="flex cursor-pointer items-start gap-2.5 rounded-md px-3 py-2.5 text-left transition-colors hover:bg-black/5"
                onClick={() => {
                  setCreatePopoverOpen(false);
                  void wr.push('/hey-syncly?mode=report');
                }}
                type="button"
              >
                <FileBarChart2 className="mt-0.5 size-4 shrink-0 text-card-foreground" />
                <div className="flex flex-col gap-0.5">
                  <span className="text-sm font-medium text-foreground">
                    Create with AI
                  </span>
                  <span className="text-xs text-muted-foreground">
                    AI-generated from your feedback data
                  </span>
                </div>
              </button>
            </PopoverContent>
          </Popover>
        }
      />

      <div
        className={cn(
          'flex min-h-0 flex-1 flex-col overflow-hidden',
          rows.length > 0 && 'overflow-y-auto',
        )}
      >
        <div className="flex items-center justify-end gap-2 px-8 pb-4 pt-8">
          <div className="relative w-[200px]">
            <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search"
              value={searchInputValue}
              onChange={(event) => setSearchInputValue(event.target.value)}
              className="h-8 pl-8 text-sm"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={Boolean(currentFolderId)}
            onClick={openCreateFolderOverlay}
          >
            <FolderPlus className="size-4" />
            Folder
          </Button>
        </div>

        <div className="px-8 pb-8">
          <DataTable
            columns={columns}
            data={rows}
            loading={
              reportListQuery.isLoading ||
              reportListQuery.isFetching ||
              (!currentFolderId && folderListQuery.isLoading)
            }
            headerClassName="bg-transparent text-gray-600"
            getRowId={(row) => row.id}
            enableRowSelection={(row) => row.original.kind === 'report'}
            rowSelection={rowSelection}
            onChangeRowSelection={setRowSelection}
            emptyState={
              <DataTableEmpty
                title={searchInputValue ? 'No results found' : 'No reports'}
                description={
                  searchInputValue
                    ? 'Try a different keyword.'
                    : 'Create a report to get started.'
                }
              />
            }
            onClickRow={(row) => {
              if (row.kind === 'parent') {
                navigateToFolder();
                return;
              }
              if (row.kind === 'folder') {
                navigateToFolder(row.folder.alias);
                return;
              }

              void wr.push(
                row.report.source_type === 'HEY_SYNCLY'
                  ? `/reports/${row.report.alias}`
                  : `/report/${row.report.alias}`,
              );
            }}
          />
          {isEmptyFolder ? (
            <DataTableEmpty
              title={searchInputValue ? 'No results found' : 'No reports'}
              description={
                searchInputValue
                  ? 'Try a different keyword.'
                  : 'Move or create a report to add it to this folder.'
              }
            />
          ) : null}
          {totalPages > 1 ? (
            <div className="pt-15">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      disabled={currentPage === 1}
                      onClick={() => {
                        if (currentPage > 1) setCurrentPage((page) => page - 1);
                      }}
                    />
                  </PaginationItem>
                  {getVisiblePages(currentPage, totalPages).map((page) =>
                    typeof page !== 'number' ? (
                      <PaginationItem key={page}>
                        <PaginationEllipsis />
                      </PaginationItem>
                    ) : (
                      <PaginationItem key={page}>
                        <PaginationLink
                          isActive={page === currentPage}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    ),
                  )}
                  <PaginationItem>
                    <PaginationNext
                      disabled={currentPage === totalPages}
                      onClick={() => {
                        if (currentPage < totalPages) {
                          setCurrentPage((page) => page + 1);
                        }
                      }}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          ) : null}
        </div>
      </div>

      {selectedReports.length > 0 ? (
        <ReportMoveActionBar
          workspaceId={workspaceId}
          currentFolderId={currentFolderId}
          folders={folders}
          selectedReports={selectedReports}
          onClear={() => setRowSelection({})}
        />
      ) : null}
    </div>
  );
};

export default ReportListPage;
